import React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import AdminRoleTable from "./AdminRoleTable";
import { deleteRolesMutation, updateRolesMutation } from "./queries/mutations";

import type { JSX } from "react";

import type { AdminRoleScope } from "./adminRoleScopes.types";
import type {
  DeleteRolesMutationFnParams,
  UpdateRolesMutationFnParams,
} from "./queries/mutations";
import type AdminUserRoles from "../../models/AdminUserRoles";

interface SingleAdminTableProps {
  userId: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => unknown;
  roles: AdminUserRoles;
}

const ROLE_SCOPE_LIST: AdminRoleScope[] = [
  "organization",
  "project",
  "channel",
];

function SingleAdminTable({
  userId,
  isLoading,
  isError,
  onRetry,
  roles,
}: SingleAdminTableProps): JSX.Element {
  const { t } = useTranslation("editor-users");

  const queryClient = useQueryClient();

  const showSuccessMessage = (): void => {
    toast.success(t("saveSuccess"));
  };

  const showErrorMessage = (statusCode?: number): void => {
    toast.error(statusCode === 403 ? t("errorForbidden") : t("saveError"));
  };

  const deleteRoles = deleteRolesMutation({
    queryClient,
    successMessage: showSuccessMessage,
    errorMessage: showErrorMessage,
  });

  const handleDeleteRoles = useMutation({
    mutationFn: async (params: DeleteRolesMutationFnParams) =>
      deleteRoles.mutationFn(params),
    onMutate: deleteRoles.onMutate,
    onSuccess: deleteRoles.onSuccess,
    onError: (error, variables, context) =>
      deleteRoles.onError(error, variables, context ?? {}),
  });

  const updateRoles = updateRolesMutation({
    queryClient,
    successMessage: showSuccessMessage,
    errorMessage: showErrorMessage,
  });

  const handleUpdateRoles = useMutation({
    mutationFn: async (params: UpdateRolesMutationFnParams) =>
      updateRoles.mutationFn(params),
    onMutate: updateRoles.onMutate,
    onSuccess: updateRoles.onSuccess,
    onError: (error, variables, context) =>
      updateRoles.onError(error, variables, context ?? {}),
  });

  return (
    <div className="flex flex-col gap-[1.875rem]">
      {ROLE_SCOPE_LIST.map((scope) => (
        <AdminRoleTable
          key={scope}
          isError={isError}
          isLoading={isLoading}
          onDeleteRoles={handleDeleteRoles.mutate}
          onRetry={onRetry}
          onUpdateRoles={handleUpdateRoles.mutate}
          roles={roles}
          scope={scope}
          userId={userId}
        />
      ))}
    </div>
  );
}

export default SingleAdminTable;
